import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, TextField,
    CircularProgress, Alert, Avatar, InputAdornment,
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    CheckCircle as ConfirmedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

const QuickMeetPublic = () => {
    const { token } = useParams();

    const [qm,            setQm]            = useState(null);
    const [loading,       setLoading]       = useState(true);
    const [notFound,      setNotFound]      = useState(false);
    const [selectedSlot,  setSelectedSlot]  = useState(null);
    const [name,          setName]          = useState('');
    const [email,         setEmail]         = useState('');
    const [submitting,    setSubmitting]    = useState(false);
    const [confirmed,     setConfirmed]     = useState(false);
    const [confirmedSlot, setConfirmedSlot] = useState(null);
    const [error,         setError]         = useState('');

    useEffect(() => {
        fetch(`${API_URL}/public/quick-meet/${token}`)
            .then(res => { if (!res.ok) throw new Error(); return res.json(); })
            .then(data => { setQm(data); setLoading(false); })
            .catch(() => { setNotFound(true); setLoading(false); });
    }, [token]);

    const handleConfirm = async (e) => {
        e.preventDefault();
        if (!selectedSlot) { setError('Please select a time.'); return; }
        if (!name.trim())  { setError('Please enter your name.'); return; }
        if (!email.trim()) { setError('Please enter your email so we can send a confirmation.'); return; }
        setError(''); setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/public/quick-meet/${token}/confirm/${selectedSlot.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ recipientName: name.trim(), recipientEmail: email.trim() }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.error || 'Something went wrong.'); return; }
            setConfirmedSlot(selectedSlot);
            setConfirmed(true);
        } catch { setError('Something went wrong. Please try again.'); }
        finally { setSubmitting(false); }
    };

    const organizer = qm
        ? (qm.organizer?.firstName && qm.organizer?.lastName
            ? `${qm.organizer.firstName} ${qm.organizer.lastName}`
            : qm.organizer?.username || 'Someone')
        : '';

    // ── States ────────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    if (notFound) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', px:3 }}>
                <Box sx={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3 }}>
                    <CalendarIcon sx={{ fontSize:32, color:'#7C3AED' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} gutterBottom>Link not found</Typography>
                <Typography color="text.secondary">This Quick Meet link doesn't exist or has expired.</Typography>
            </Box>
        </Box>
    );

    // Already confirmed by someone else
    if (qm.status === 'CONFIRMED' && !confirmed) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', maxWidth:400, px:3 }}>
                <Box sx={{ width:80, height:80, borderRadius:'50%', background:'linear-gradient(135deg,#DCFCE7,#BBF7D0)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3 }}>
                    <ConfirmedIcon sx={{ fontSize:38, color:'#16A34A' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} gutterBottom>Already confirmed</Typography>
                <Typography color="text.secondary">This meeting has already been booked. Thanks!</Typography>
            </Box>
        </Box>
    );

    // Success
    if (confirmed && confirmedSlot) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', maxWidth:400, px:3 }}>
                <Box sx={{ width:88, height:88, borderRadius:'50%', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3, boxShadow:'0 12px 32px rgba(124,58,237,0.3)' }}>
                    <Typography sx={{ fontSize:40 }}>🎉</Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color:'#0F172A' }}>You're confirmed!</Typography>
                <Typography color="text.secondary" sx={{ mb:3, lineHeight:1.7 }}>
                    Your meeting with <strong>{organizer}</strong> is all set.
                    {email && ' A confirmation has been sent to your email.'}
                </Typography>
                <Paper elevation={0} variant="outlined" sx={{ borderRadius:3, p:3, textAlign:'left', borderColor:'rgba(0,0,0,0.1)' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5 }}>Meeting</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{qm.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt:1 }}>
                        {format(new Date(confirmedSlot.proposedStartTime), 'EEEE, MMMM d, yyyy')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {format(new Date(confirmedSlot.proposedStartTime), 'h:mm a')} – {format(new Date(confirmedSlot.proposedEndTime), 'h:mm a')}
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );

    // ── Main view ─────────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC' }}>

            {/* Header */}
            <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', py:5, position:'relative', overflow:'clip' }}>
                <Box sx={{ position:'absolute', top:-40, left:'-3%', width:280, height:280, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', bottom:-30, right:'5%', width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />
                <Box sx={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:1.5, px:3 }}>
                    <Avatar sx={{ width:64, height:64, fontSize:28, fontWeight:800, bgcolor:'rgba(255,255,255,0.18)', border:'2px solid rgba(255,255,255,0.3)' }}>
                        {organizer[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>
                            {organizer} wants to meet
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color:'rgba(255,255,255,0.85)', mt:0.5 }}>
                            {qm.title}
                        </Typography>
                    </Box>
                    <Box sx={{ px:2, py:0.5, borderRadius:99, bgcolor:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)' }}>
                        <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.9)', fontWeight:600 }}>
                            {qm.durationMinutes} min · Pick a time below
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ maxWidth:540, mx:'auto', px:3, py:5, display:'flex', flexDirection:'column', gap:3 }}>

                {qm.note && (
                    <Paper elevation={0} variant="outlined" sx={{ borderRadius:3, p:3, bgcolor:'#F5F3FF', borderColor:'#EDE9FE' }}>
                        <Typography variant="body2" sx={{ color:'#4C1D95', lineHeight:1.7 }}>"{qm.note}"</Typography>
                    </Paper>
                )}

                {/* Time options */}
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb:2, color:'#0F172A' }}>
                        Which time works for you?
                    </Typography>
                    <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
                        {qm.slots?.map(slot => {
                            const isSel = selectedSlot?.id === slot.id;
                            return (
                                <Box key={slot.id} onClick={() => setSelectedSlot(isSel ? null : slot)} sx={{
                                    px:3, py:2.5, borderRadius:3, cursor:'pointer',
                                    border:'2px solid',
                                    borderColor: isSel ? '#7C3AED' : '#E5E7EB',
                                    bgcolor: isSel ? '#7C3AED' : 'white',
                                    transition:'all 0.15s',
                                    '&:hover':{ borderColor:'#7C3AED', bgcolor: isSel ? '#6D28D9' : '#EDE9FE' },
                                }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: isSel?'white':'#0F172A' }}>
                                        {format(new Date(slot.proposedStartTime), 'EEEE, MMMM d')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: isSel?'rgba(255,255,255,0.8)':'text.secondary', mt:0.25 }}>
                                        {format(new Date(slot.proposedStartTime), 'h:mm a')} – {format(new Date(slot.proposedEndTime), 'h:mm a')}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Confirm form */}
                {selectedSlot && (
                    <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>
                        <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)' }}>
                            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700 }}>
                                Confirm your spot
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color:'white' }}>
                                {format(new Date(selectedSlot.proposedStartTime), 'EEE, MMM d · h:mm a')}
                            </Typography>
                        </Box>
                        <Box sx={{ px:3, py:3, bgcolor:'white' }}>
                            {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}
                            <Box component="form" onSubmit={handleConfirm} sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Your name *</Typography>
                                    <TextField fullWidth variant="outlined" required size="small"
                                        placeholder="What's your name?"
                                        value={name} onChange={e => setName(e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Email</Typography>
                                    <TextField fullWidth variant="outlined" type="email" size="small" required
                                        placeholder="you@example.com"
                                        value={email} onChange={e => setEmail(e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                                </Box>
                                <Button type="submit" variant="contained" size="large" disabled={submitting}
                                    sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)', py:1.5, '&:hover':{ boxShadow:'0 6px 20px rgba(124,58,237,0.45)' } }}>
                                    {submitting ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Confirm Meeting'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign:'center' }}>
                                    No account needed. First to confirm gets the spot.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    );
};

export default QuickMeetPublic;
